import React, { useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Image from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import ReferenceBubbleMenu from '@tiptap/extension-bubble-menu'
import ReferenceFloatingMenu from '@tiptap/extension-floating-menu'
// In Tiptap v3, these are in a sub-export
// @ts-ignore - Module 'menus' exists in exports but TS with moduleResolution:node fails to find it
import { BubbleMenu } from '@tiptap/react/menus'
import { MediaManagerModal } from '../MediaManagerModal'
import { cleanTiptapOutput } from '../builder/htmlUtils'
import './TipTapEditor.css'
import { Extension } from '@tiptap/core'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  TEMPLATE_MESSAGES as M,
  HEADING_1,
  HEADING_2,
  HEADING_3,
  asToken,
} from '../../templateMessages'

interface TipTapEditorProps {
  value: string
  onChange: (html: string) => void
  height?: number
  onInsertVariable?: (variableKey: string) => void
  variables?: string[]
  primaryColor?: string
  onEditorReady?: (editor: any) => void
}

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {}
              }

              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },
})

const EMAIL_SAFE_FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px']
const EMAIL_SAFE_TEXT_COLORS = ['#111827', '#374151', '#6B7280', '#1D4ED8', '#047857', '#B45309', '#B91C1C']

export const TipTapEditor: React.FC<TipTapEditorProps> = ({
  value,
  onChange,
  height = 400,
  onInsertVariable,
  variables = [],
  primaryColor = '#007bff',
  onEditorReady,
}) => {
  const intl = useIntl()
  const [showMediaManager, setShowMediaManager] = useState(false)
  const [showVariableDropdown, setShowVariableDropdown] = useState(false)
  const [showIconDropdown, setShowIconDropdown] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      TextStyle,
      FontSize,
      Color,
      Image.extend({
        addAttributes() {
          return {
            // Keep default Image attributes (src, alt, title)
            src: {
              default: null,
            },
            alt: {
              default: null,
            },
            title: {
              default: null,
            },
            // Add custom attributes
            style: {
              default: null,
              parseHTML: element => element.getAttribute('style'),
              renderHTML: attributes => {
                if (!attributes.style) {
                  return {}
                }
                return { style: attributes.style }
              },
            },
            width: {
              default: null,
              parseHTML: element => element.getAttribute('width'),
              renderHTML: attributes => {
                if (!attributes.width) {
                  return {}
                }
                return { width: attributes.width }
              },
            },
            height: {
              default: null,
              parseHTML: element => element.getAttribute('height'),
              renderHTML: attributes => {
                if (!attributes.height) {
                  return {}
                }
                return { height: attributes.height }
              },
            },
          }
        },
      }),
      Table.extend({
        addAttributes() {
          return {
            // @ts-ignore
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: element => element.getAttribute('style'),
              renderHTML: attributes => {
                if (!attributes.style) {
                  return {}
                }
                return { style: attributes.style }
              },
            },
          }
        },
      }).configure({
        resizable: false,
      }),
      TableRow,
      TableCell,
      TableHeader,
      ReferenceBubbleMenu,
      ReferenceFloatingMenu,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const dirtyHtml = editor.getHTML()
      const cleanHtml = cleanTiptapOutput(dirtyHtml)
      onChange(cleanHtml)
    },
    onCreate: ({ editor }) => {
      if (onEditorReady) {
        onEditorReady(editor)
      }
    }
  }) // End useEditor

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  if (!editor) {
    return null
  }

  const currentFontSize = editor.getAttributes('textStyle')?.fontSize || ''
  const currentTextColor = editor.getAttributes('textStyle')?.color || ''

  const setLink = () => {
    const url = window.prompt('URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  const addImage = () => {
    setShowMediaManager(true)
  }

  const handleMediaSelect = (url: string) => {
    // Prompt for dimensions
    const width = window.prompt('Image Width (e.g., 300px, 100%, auto):', 'auto')
    const height = window.prompt('Image Height (e.g., 200px, auto):', 'auto')

    // Build style attribute
    const styles: string[] = []
    if (width && width !== 'auto') {
      styles.push(`width: ${width}`)
    }
    if (height && height !== 'auto') {
      styles.push(`height: ${height}`)
    }

    // Add max-width for responsiveness
    if (!width || width === 'auto') {
      styles.push('max-width: 100%')
    }

    const styleString = styles.length > 0 ? styles.join('; ') : undefined

    // Insert image with optional style
    if (styleString) {
      // @ts-ignore - Extended Image extension supports style attribute
      editor.chain().focus().setImage({ src: url, style: styleString }).run()
    } else {
      editor.chain().focus().setImage({ src: url }).run()
    }

    setShowMediaManager(false)
  }

  const addDivider = () => {
    editor.chain().focus().setHorizontalRule().run()
  }

  const addCTA = () => {
    const url = window.prompt('Button Link URL:')
    if (!url) return
    const text = window.prompt('Button Text:', 'Click Here')
    if (!text) return

    // Insert a styled link as a "button"
    // Using inline-block and specific padding/background for a button look
    const buttonHtml = `<a href="${url}" style="background-color: ${primaryColor || '#007bff'}; color: #ffffff; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; font-family: sans-serif; font-size: 16px; margin: 5px 0;">${text}</a>`

    editor.chain().focus().insertContent(buttonHtml).run()
  }



  const insertVariable = (variableKey: string) => {
    const variableText = `{{${variableKey}}}`
    editor.chain().focus().insertContent(variableText).run()
    setShowVariableDropdown(false)
  }

  // --- Layout Blocks Helpers (Odoo-style) ---

  const addImageCallback = (url: string) => {
    handleMediaSelect(url)
  }

  // 2 Columns: Image | Text
  const addLayoutImageText = () => {
    editor.chain().focus().insertTable({ rows: 1, cols: 2, withHeaderRow: false }).run()
    // We can't easily force content types into cells immediately with standard table extension
    // But we create the structure. The user can then drop image in left, text in right.
  }

  // 3 Columns
  const addLayout3Cols = () => {
    editor.chain().focus().insertTable({ rows: 1, cols: 3, withHeaderRow: false }).run()
  }

  const addHeroSection = () => {
    // A Hero is just a big image followed by a header
    // We open media manager, user picks image, then we add a H1 below it
    setShowMediaManager(true)
    // We might need a flag to know we are in "Hero Mode" to append text after, 
    // but for now let's just let them pick the image and they can add text.
  }

  const openMediaManager = () => {
    setShowMediaManager(true)
  }

  return (
    <div className='tiptap-editor-wrapper'>
      {/* Toolbar */}
      <div className='tiptap-toolbar'>
        {/* Text Formatting */}
        <div className='btn-group' role='group'>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('bold') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteBold)}
          >
            <i className='bi bi-type-bold'></i>
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('italic') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteItalic)}
          >
            <i className='bi bi-type-italic'></i>
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('underline') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteUnderline)}
          >
            <i className='bi bi-type-underline'></i>
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('strike') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteStrike)}
          >
            <i className='bi bi-type-strikethrough'></i>
          </button>
        </div>

        {/* Headings */}
        <div className='btn-group ms-2' role='group'>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
            title={intl.formatMessage(M.rteHeading1)}
          >
            {HEADING_1}
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
            title={intl.formatMessage(M.rteHeading2)}
          >
            {HEADING_2}
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
            title={intl.formatMessage(M.rteHeading3)}
          >
            {HEADING_3}
          </button>
        </div>

        <div className='ms-2 d-flex align-items-center'>
          <select
            className='form-select form-select-sm tiptap-fontsize-select'
            value={currentFontSize}
            onChange={(event) => {
              const nextValue = event.target.value
              if (!nextValue) {
                editor.chain().focus().setMark('textStyle', { fontSize: null }).run()
                return
              }

              editor.chain().focus().setMark('textStyle', { fontSize: nextValue }).run()
            }}
            title={intl.formatMessage(M.rteFontSize)}
          >
            <option value=''>{intl.formatMessage(M.rteFontSize)}</option>
            {EMAIL_SAFE_FONT_SIZES.map((fontSize) => (
              <option key={fontSize} value={fontSize}>
                {fontSize}
              </option>
            ))}
          </select>
        </div>

        <div className='ms-2 d-flex align-items-center gap-2'>
          <input
            type='color'
            className='form-control form-control-color tiptap-color-input'
            value={currentTextColor || '#111827'}
            onChange={(event) => {
              editor.chain().focus().setColor(event.target.value.toUpperCase()).run()
            }}
            title={intl.formatMessage(M.rteTextColor)}
          />
          <select
            className='form-select form-select-sm tiptap-color-select'
            value={currentTextColor || ''}
            onChange={(event) => {
              const nextValue = event.target.value
              if (!nextValue) {
                editor.chain().focus().unsetColor().run()
                return
              }

              editor.chain().focus().setColor(nextValue).run()
            }}
            title={intl.formatMessage(M.rteTextColor)}
          >
            <option value=''>{intl.formatMessage(M.rteTextColor)}</option>
            {EMAIL_SAFE_TEXT_COLORS.map((color) => (
              <option key={color} value={color}>
                {color}
              </option>
            ))}
          </select>
        </div>

        {/* Lists */}
        <div className='btn-group ms-2' role='group'>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('bulletList') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteBulletList)}
          >
            <i className='bi bi-list-ul'></i>
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('orderedList') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteNumberedList)}
          >
            <i className='bi bi-list-ol'></i>
          </button>
        </div>

        {/* Alignment */}
        <div className='btn-group ms-2' role='group'>
          <button
            type='button'
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
            title={intl.formatMessage(M.rteAlignLeft)}
          >
            <i className='bi bi-text-left'></i>
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
            title={intl.formatMessage(M.rteAlignCenter)}
          >
            <i className='bi bi-text-center'></i>
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
            title={intl.formatMessage(M.rteAlignRight)}
          >
            <i className='bi bi-text-right'></i>
          </button>
        </div>

        {/* More Options */}
        <div className='btn-group ms-2' role='group'>
          <button
            type='button'
            onClick={setLink}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('link') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteInsertLink)}
          >
            <i className='bi bi-link-45deg'></i>
          </button>
          <button
            type='button'
            onClick={openMediaManager}
            className='btn btn-sm btn-outline-secondary'
            title={intl.formatMessage(M.rteInsertImage)}
          >
            <i className='bi bi-image'></i>
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`btn btn-sm btn-outline-secondary ${editor.isActive('blockquote') ? 'active' : ''}`}
            title={intl.formatMessage(M.rteBlockquote)}
          >
            <i className='bi bi-quote'></i>
          </button>
          <button
            type='button'
            onClick={addCTA}
            className='btn btn-sm btn-outline-secondary'
            title={intl.formatMessage(M.rteInsertButton)}
          >
            <i className='bi bi-box-arrow-in-right'></i>
          </button>
        </div>




        {/* Variable Selector */}
        {variables.length > 0 && (
          <div className='btn-group ms-2' role='group'>
            <div className='position-relative'>
              <button
                type='button'
                onClick={() => {
                  setShowVariableDropdown(!showVariableDropdown)
                  setShowIconDropdown(false)
                }}
                className='btn btn-sm btn-outline-secondary'
                title={intl.formatMessage(M.rteInsertVariable)}
              >
                <i className='bi bi-braces'></i>
                <span className='ms-1'><FormattedMessage {...M.variables} /></span>
              </button>
              {showVariableDropdown && (
                <div className='variable-picker-dropdown' style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, background: 'white', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px', minWidth: '200px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                  <div className='d-flex justify-content-between align-items-center mb-2 border-bottom pb-1'>
                    <span className='text-muted small fw-bold'><FormattedMessage {...M.availableVariables} /></span>
                    <button
                      type='button'
                      className='btn btn-sm btn-close'
                      onClick={() => setShowVariableDropdown(false)}
                      style={{ padding: '0.25rem' }}
                    ></button>
                  </div>
                  <div className='d-flex flex-column gap-1 max-h-200px overflow-auto'>
                    {variables.map((variable) => (
                      <button
                        key={variable}
                        type='button'
                        className='btn btn-sm btn-light text-start border-0'
                        onClick={() => insertVariable(variable)}
                      >
                        <i className='bi bi-braces text-primary me-2'></i>
                        <code>{asToken(variable)}</code>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Icon/Emoji Picker */}
        <div className='btn-group ms-2' role='group'>
          <div className='position-relative'>
            <button
              type='button'
              onClick={() => {
                // @ts-ignore - TS doesn't know about the dynamically added state yet
                setShowIconDropdown(!showIconDropdown)
                setShowVariableDropdown(false)
              }}
              className='btn btn-sm btn-outline-secondary'
              title={intl.formatMessage(M.rteInsertIcon)}
            >
              <i className='bi bi-emoji-smile'></i>
              <span className='ms-1'><FormattedMessage {...M.rteIcons} /></span>
            </button>
            {/* @ts-ignore */}
            {showIconDropdown && (
              <div className='icon-picker-dropdown' style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, background: 'white', border: '1px solid #dee2e6', borderRadius: '4px', padding: '12px', width: '260px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <div className='d-flex justify-content-between align-items-center mb-2 border-bottom pb-1'>
                  <span className='text-muted small fw-bold'><FormattedMessage {...M.rteIconsGroup} /></span>
                  <button
                    type='button'
                    className='btn btn-sm btn-close'
                    // @ts-ignore
                    onClick={() => setShowIconDropdown(false)}
                    style={{ padding: '0.25rem' }}
                  ></button>
                </div>
                <div className='d-flex flex-wrap gap-2'>
                  {[
                    { char: '📞', title: M.iconPhone }, { char: '📱', title: M.iconMobile }, { char: '✉️', title: M.iconEmail },
                    { char: '💬', title: M.iconChat }, { char: '📍', title: M.iconLocation }, { char: '🌐', title: M.iconWeb },
                    { char: '✅', title: M.iconCheck }, { char: '⭐', title: M.iconStar }, { char: '📅', title: M.iconCalendar },
                    { char: '🕒', title: M.iconClock }, { char: '⚠️', title: M.iconWarning }, { char: '➡️', title: M.iconArrow },
                    { char: '📋', title: M.iconList }, { char: '🏥', title: M.iconHealth }, { char: '🛡️', title: M.iconInsurance }
                  ].map((icon, i) => (
                    <button
                      key={i}
                      type='button'
                      className='btn btn-light border-0 fs-5'
                      style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title={intl.formatMessage(icon.title)}
                      onClick={() => {
                        editor.chain().focus().insertContent(icon.char).run()
                        // @ts-ignore
                        setShowIconDropdown(false)
                      }}
                    >
                      {icon.char}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Clear Formatting */}
        <div className='btn-group ms-2' role='group'>
          <button
            type='button'
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
            className='btn btn-sm btn-outline-secondary'
            title={intl.formatMessage(M.rteClearFormatting)}
          >
            <i className='bi bi-eraser'></i>
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <div className='tiptap-content' style={{ minHeight: `${height}px` }}>
        {editor && (
          <>
            <BubbleMenu editor={editor} className="bubble-menu-container">
              <div className="btn-group shadow bg-white rounded border">
                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`btn btn-sm ${editor.isActive('bold') ? 'btn-primary' : 'btn-light'}`}><i className="bi bi-type-bold" /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`btn btn-sm ${editor.isActive('italic') ? 'btn-primary' : 'btn-light'}`}><i className="bi bi-type-italic" /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={`btn btn-sm ${editor.isActive('strike') ? 'btn-primary' : 'btn-light'}`}><i className="bi bi-type-strikethrough" /></button>
                <button type="button" onClick={setLink} className={`btn btn-sm ${editor.isActive('link') ? 'btn-primary' : 'btn-light'}`}><i className="bi bi-link-45deg" /></button>
              </div>
            </BubbleMenu>
          </>
        )}
        <EditorContent editor={editor} />
      </div>

      {/* Media Manager Integration */}
      {showMediaManager && (
        <MediaManagerModal
          isOpen={showMediaManager}
          onClose={() => setShowMediaManager(false)}
          onSelect={addImageCallback}
        />
      )}
    </div>
  )
}
